'use client';

import styled from '@emotion/styled';
import { spacing } from '@/theme/tokens';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEntraAuth } from './hooks/useEntraAuth';
import NavigationLink from './common/NavigationLink';

type NavLink = {
    text: string;
    href: string;
    activePath?: string;
};

const Wrapper = styled.nav`
    height: 100%;

    @media (max-width: 1147px) {
        display: none;
    }
`;

const NavList = styled.ul`
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: ${spacing[32]};
    height: 100%;
    list-style: none;
    margin: 0;
    padding: 0;
`;

const NavItem = styled.li`
    padding-bottom: 0;
`;

function MainNavigation() {
    const t = useTranslations('MainNavigation');
    const pathname = usePathname();
    const { isAuthenticated } = useEntraAuth();
    const locale = pathname.split('/')[1];

    const navLinks: NavLink[] = [
        { text: t('cargo'), href: `/${locale}/cargo` },
        { text: t('company'), href: `/${locale}/company/about-us`, activePath: `/${locale}/company` },
        { text: t('services'), href: `/${locale}/services/general-info`, activePath: `/${locale}/services` },
        { text: t('contacts'), href: `/${locale}/contacts` },
        { text: t('autosales'), href: `/${locale}/gg-autosales/about-us`, activePath: `/${locale}/gg-autosales` },
        ...(isAuthenticated ? [{ text: t('customerZone'), href: `/${locale}/customer/` }] : []),
    ];

    const isActive = (path: string): boolean => {
        const normalizedPathname = pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
        const normalizedPath = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;

        return normalizedPathname === normalizedPath || normalizedPathname.startsWith(`${normalizedPath}/`);
    };

    return (
        <Wrapper>
            <NavList>
                {navLinks.map(({ text, href, activePath }) => (
                    <NavItem key={href}>
                        <NavigationLink href={href} isActive={isActive(activePath ?? href)}>
                            {text}
                        </NavigationLink>
                    </NavItem>
                ))}
            </NavList>
        </Wrapper>
    );
}

export default MainNavigation;
